import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BrandFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  brand = null, 
  mode = 'create',
  existingBrands = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (brand && mode === 'edit') {
      setFormData({
        name: brand?.name || '',
        code: brand?.code || '',
        description: brand?.description || ''
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: ''
      });
    }
    setErrors({});
  }, [brand, mode, isOpen]);

  const handleInputChange = (field, value) => {
    if (field === 'code') {
      // Convert to uppercase and limit to 3 characters
      value = value?.toUpperCase()?.slice(0, 3);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Brand name is required';
    }

    if (!formData?.code?.trim()) {
      newErrors.code = 'Brand code is required';
    } else if (formData?.code?.length < 2) {
      newErrors.code = 'Brand code must be at least 2 characters';
    } else if (formData?.code?.length > 3) {
      newErrors.code = 'Brand code must be at most 3 characters';
    } else if (!/^[A-Z]+$/?.test(formData?.code)) {
      newErrors.code = 'Brand code must contain only uppercase letters';
    }

    // Check for duplicate codes (excluding current brand in edit mode)
    const duplicateCode = existingBrands?.find(b => 
      b?.code?.toUpperCase() === formData?.code?.toUpperCase() && 
      (mode === 'create' || b?.id !== brand?.id)
    );
    if (duplicateCode) {
      newErrors.code = 'This brand code is already in use';
    }

    // Check for duplicate names (excluding current brand in edit mode)
    const duplicateName = existingBrands?.find(b => 
      b?.name?.toLowerCase()?.trim() === formData?.name?.toLowerCase()?.trim() && 
      (mode === 'create' || b?.id !== brand?.id)
    );
    if (duplicateName) {
      newErrors.name = 'This brand name is already in use';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting brand form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateCodeSuggestion = (name) => {
    if (!name?.trim()) return '';
    
    // Remove common words and extract meaningful parts
    const words = name?.trim()?.split(/\s+/)?.filter(word => 
      !['the', 'and', 'of', 'for', 'in', 'on', 'at', 'to', 'a', 'an']?.includes(word?.toLowerCase())
    );
    
    if (words?.length === 1) {
      return words?.[0]?.substring(0, 3)?.toUpperCase();
    } else if (words?.length === 2) {
      return (words?.[0]?.charAt(0) + words?.[1]?.substring(0, 2))?.toUpperCase();
    } else {
      return words?.slice(0, 3)?.map(word => word?.charAt(0))?.join('')?.toUpperCase();
    }
  };

  const handleNameChange = (value) => {
    handleInputChange('name', value);
    
    // Auto-suggest code if creating new brand and code is empty
    if (mode === 'create' && !formData?.code?.trim()) {
      const suggestion = generateCodeSuggestion(value);
      if (suggestion) {
        handleInputChange('code', suggestion);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Tag" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {mode === 'edit' ? 'Edit Brand' : 'Add New Brand'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {mode === 'edit' ? 'Update brand information' : 'Create a new brand with unique code'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Brand Name"
            type="text"
            placeholder="Enter brand name"
            value={formData?.name}
            onChange={(e) => handleNameChange(e?.target?.value)}
            error={errors?.name}
            required
          />

          <div>
            <Input
              label="Brand Code"
              type="text"
              placeholder="2-3 letters (e.g., LIS, CAV)"
              value={formData?.code}
              onChange={(e) => handleInputChange('code', e?.target?.value)}
              error={errors?.code}
              required
              maxLength={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              A 2-3 letter abbreviation that sounds close to the brand name. This will be used to generate KV IDs.
            </p>
          </div>

          <Input
            label="Description (Optional)"
            type="text"
            placeholder="Brief description of the brand"
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
          />

          {/* Code Preview */}
          {formData?.code?.trim() && (
            <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Hash" size={14} className="text-accent" />
                <span className="text-sm font-medium text-accent">KV ID Preview:</span>
                <span className="font-mono text-accent">{formData?.code}-XX-001</span>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName={mode === 'edit' ? 'Save' : 'Plus'}
              iconPosition="left"
            >
              {mode === 'edit' ? 'Update Brand' : 'Add Brand'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandFormModal;